import { h } from "vue";

/**
 * Returns the alignment modifier for a column, or undefined when it aligns to the start.
 *
 * @param {Object} column
 * @returns {String|undefined}
 */
export function cellClass(column) {
  return column.align ? `--align-${column.align}` : undefined;
}

/**
 * Returns the inline style carrying a column's fixed width, or undefined when it sizes itself.
 *
 * @param {Object} column
 * @returns {Object|undefined}
 */
export function cellStyle(column) {
  return column.width ? { width: column.width } : undefined;
}

/**
 * Returns the key identifying a row. `rowKey` names the field holding a stable id; the index stands
 * in when no field is named and when the named one holds nothing, which would otherwise key every
 * such row alike and reuse one row's DOM for another.
 *
 * @param {Object} item
 * @param {Number} index
 * @param {String} [rowKey]
 * @returns {String|Number}
 */
export function rowKeyFor(item, index, rowKey) {
  const key = rowKey ? item?.[rowKey] : undefined;

  // An empty string names no row, so it falls back rather than keying every such row alike. A falsy
  // but real key such as 0 is kept.
  return key === undefined || key === null || key === "" ? index : key;
}

/**
 * Renders one cell's content.
 *
 * A cell's content cannot arrive through a slot: slot content is authored outside the element and so
 * never sees the row. `formatter(value, row)` is the way in, and it runs inside a render, so a
 * consumer ref it reads still tracks.
 *
 * @param {Object} column
 * @param {Object} item
 * @returns {Object|String} a VNode or the value's text
 */
function renderCell(column, item) {
  const value = item?.[column.field];

  if (typeof column.formatter !== "function") {
    return h("span", value == null ? "" : String(value));
  }

  return column.formatter(value, item);
}

/**
 * The cell's content as a component whose identity never changes.
 *
 * Vue keys a subtree on its component type, so rendering each cell through a function built during
 * render would make every parent update a different type, remounting the cell: an input a formatter
 * rendered would lose focus and its own state whenever anything else on the table changed.
 */
export const TableCell = (props) => renderCell(props.column, props.item);

TableCell.props = {
  column: { type: Object, required: true },
  item: { type: Object, required: true },
};

/**
 * A row's expansion content, as a component whose identity never changes, for the reason `TableCell` gives.
 */
export const TableExpansion = (props) => props.expansion(props.item);

TableExpansion.props = {
  expansion: { type: Function, required: true },
  item: { type: Object, required: true },
};
